import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FlaskRound, Plus, Search, AlertTriangle, ShieldAlert, FileWarning } from "lucide-react";

const EMPTY = {
  name: "", category: "Stain", manufacturer: "", catalog_number: "", department: "Histology",
  unit: "mL", storage: "Room temperature", hazard_class: "Non-hazardous",
  reorder_level: 0, qc_interval_days: 0, requires_new_lot_qc: true, notes: "",
};

export default function Reagents() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({ q: "", category: "", department: "", alert: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { ...filters };
      Object.keys(params).forEach((k) => !params[k] && delete params[k]);
      const [list, sum] = await Promise.all([
        api.get("/reagents", { params }),
        api.get("/reagents/summary"),
      ]);
      setRows(list.data);
      setSummary(sum.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [filters]);

  useEffect(() => { api.get("/reagents/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const canManage = ["admin", "lab_director", "supervisor", "pathologist", "pathologist_assistant", "tech"].includes(user?.role);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/reagents", {
        ...form,
        reorder_level: Number(form.reorder_level) || 0,
        qc_interval_days: Number(form.qc_interval_days) || 0,
      });
      toast.success(`${data.name} added to the catalog`);
      setOpen(false);
      setForm({ ...EMPTY });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const Alert = ({ label, value, tone, testid, alert }) => (
    <button onClick={() => setFilters((f) => ({ ...f, alert: f.alert === alert ? "" : alert }))}
            data-testid={testid}
            className={`border p-3 text-left transition-colors ${filters.alert === alert ? "border-[#134e4a] bg-[#134e4a]/5" : "border-slate-200 bg-white hover:border-[#134e4a]"}`}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`num mt-1 text-2xl font-bold ${value ? tone : "text-slate-900"}`}>{value}</div>
    </button>
  );

  return (
    <div className="space-y-6" data-testid="reagents-page">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#134e4a]">ChemTraX</p>
          <h1 className="mt-1 flex items-center gap-2 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            <FlaskRound size={26} className="text-[#134e4a]" /> Reagent Management
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Lot and expiry tracking, QC records, manufacturer details, safety data sheets and low-stock alerts.
          </p>
        </div>
        {canManage && (
          <Button onClick={() => setOpen(true)} data-testid="new-reagent-button"
                  className="ml-auto h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
            <Plus size={16} className="mr-2" /> Add chemical
          </Button>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-6" data-testid="reagents-alerts">
          <Alert label="Expired" value={summary.expired} tone="text-red-800" testid="reagent-alert-expired" alert="expired" />
          <Alert label={`Expiring ≤${summary.expiry_warn_days}d`} value={summary.expiring_soon} tone="text-amber-700" testid="reagent-alert-expiring" alert="expiring_soon" />
          <Alert label="Low stock" value={summary.low_stock} tone="text-amber-700" testid="reagent-alert-low-stock" alert="low_stock" />
          <Alert label="QC overdue" value={summary.qc_overdue} tone="text-amber-700" testid="reagent-alert-qc-overdue" alert="qc_overdue" />
          <Alert label="Quarantined" value={summary.quarantined} tone="text-red-800" testid="reagent-alert-quarantined" alert="quarantined" />
          <Alert label="No SDS on file" value={summary.missing_sds} tone="text-red-800" testid="reagent-alert-missing-sds" alert="missing_sds" />
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 border border-slate-200 bg-slate-50 p-3">
        <div className="min-w-[220px] flex-1">
          <Label className="text-xs uppercase tracking-wide text-slate-600">Search</Label>
          <div className="relative mt-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                   data-testid="reagent-search-input" placeholder="Name, manufacturer or catalog number"
                   className="h-11 border-slate-300 pl-9" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Category</Label>
          <select value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
                  data-testid="reagent-category-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">All categories</option>
            {(meta?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Department</Label>
          <select value={filters.department} onChange={(e) => setFilters((f) => ({ ...f, department: e.target.value }))}
                  data-testid="reagent-department-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">All departments</option>
            {(meta?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <Button variant="outline" onClick={() => setFilters({ q: "", category: "", department: "", alert: "" })}
                data-testid="reagent-clear-filters" className="h-11 border-slate-300">Clear</Button>
      </div>

      <div className="border border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-sm" data-testid="reagents-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="px-3 py-2">Reagent</th><th className="px-3 py-2">Manufacturer</th>
                <th className="px-3 py-2">Catalog #</th><th className="px-3 py-2">Category</th>
                <th className="px-3 py-2 text-right">On hand</th><th className="px-3 py-2 text-right">Lots</th>
                <th className="px-3 py-2">Flags</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 row-hover hover:bg-slate-50" data-testid={`reagent-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <Link to={`/reagents/${r.id}`} className="font-semibold text-[#134e4a] hover:underline"
                          data-testid={`reagent-link-${r.id}`}>{r.name}</Link>
                    <div className="text-[11px] text-slate-500">{r.storage} · {r.hazard_class}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{r.manufacturer || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-600">{r.catalog_number || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{r.category}</td>
                  <td className={`num px-3 py-2 text-right font-semibold ${r.low_stock ? "text-amber-700" : "text-slate-800"}`}>
                    {r.quantity_on_hand} {r.unit}
                  </td>
                  <td className="num px-3 py-2 text-right text-slate-700">{r.active_lot_count}/{r.lot_count}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {r.expired_lots > 0 && <span className="bg-red-800 px-1.5 py-0.5 text-[11px] font-semibold text-white" data-testid={`flag-expired-${r.id}`}>{r.expired_lots} expired</span>}
                      {r.expiring_lots > 0 && <span className="bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900">{r.expiring_lots} expiring</span>}
                      {r.low_stock && <span className="bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900" data-testid={`flag-low-${r.id}`}>Low stock</span>}
                      {r.qc_overdue_lots > 0 && <span className="bg-sky-100 px-1.5 py-0.5 text-[11px] font-semibold text-sky-900">QC overdue</span>}
                      {r.quarantined_lots > 0 && <span className="inline-flex items-center gap-1 bg-red-100 px-1.5 py-0.5 text-[11px] font-semibold text-red-900"><ShieldAlert size={11} /> Quarantined</span>}
                      {r.missing_sds && <span className="inline-flex items-center gap-1 bg-slate-200 px-1.5 py-0.5 text-[11px] font-semibold text-slate-800" data-testid={`flag-no-sds-${r.id}`}><FileWarning size={11} /> No SDS</span>}
                      {!r.expired_lots && !r.expiring_lots && !r.low_stock && !r.qc_overdue_lots && !r.quarantined_lots && !r.missing_sds && (
                        <span className="text-[11px] text-slate-400">—</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <div className="px-3 py-10 text-center" data-testid="reagents-empty">
            <AlertTriangle size={18} className="mx-auto mb-2 text-slate-400" />
            <p className="text-sm text-slate-500">
              {filters.q || filters.category || filters.department || filters.alert
                ? "No chemicals match these filters."
                : "No chemicals in the catalog yet."}
            </p>
            {canManage && (
              <Button onClick={() => setOpen(true)} data-testid="empty-add-reagent-button"
                      className="mt-4 h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                <Plus size={16} className="mr-2" /> Add your first chemical
              </Button>
            )}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="new-reagent-dialog">
          <DialogHeader>
            <DialogTitle>New reagent</DialogTitle>
            <DialogDescription>Catalog the reagent once, then receive lots against it and record QC per lot.</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                     data-testid="reagent-name-input" required minLength={2}
                     placeholder="e.g. Harris Hematoxylin" className="mt-1 h-11 border-slate-300" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Manufacturer</Label>
                <Input value={form.manufacturer} onChange={(e) => setForm((f) => ({ ...f, manufacturer: e.target.value }))}
                       data-testid="reagent-manufacturer-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Catalog number</Label>
                <Input value={form.catalog_number} onChange={(e) => setForm((f) => ({ ...f, catalog_number: e.target.value }))}
                       data-testid="reagent-catalog-input" className="mt-1 h-11 border-slate-300" />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Category</Label>
                <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                        data-testid="reagent-category-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Department</Label>
                <select value={form.department} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
                        data-testid="reagent-department-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Unit</Label>
                <select value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
                        data-testid="reagent-unit-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.units || []).map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Storage</Label>
                <select value={form.storage} onChange={(e) => setForm((f) => ({ ...f, storage: e.target.value }))}
                        data-testid="reagent-storage-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.storage_conditions || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Hazard class</Label>
                <select value={form.hazard_class} onChange={(e) => setForm((f) => ({ ...f, hazard_class: e.target.value }))}
                        data-testid="reagent-hazard-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.hazard_classes || []).map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Reorder level ({form.unit})</Label>
                <Input type="number" min="0" step="any" value={form.reorder_level}
                       onChange={(e) => setForm((f) => ({ ...f, reorder_level: e.target.value }))}
                       data-testid="reagent-reorder-input" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Recurring QC every (days, 0 = none)</Label>
                <Input type="number" min="0" max="365" value={form.qc_interval_days}
                       onChange={(e) => setForm((f) => ({ ...f, qc_interval_days: e.target.value }))}
                       data-testid="reagent-qc-interval-input" className="mt-1 h-11 border-slate-300" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.requires_new_lot_qc}
                     onChange={(e) => setForm((f) => ({ ...f, requires_new_lot_qc: e.target.checked }))}
                     data-testid="reagent-requires-qc-input" className="h-4 w-4 accent-[#134e4a]" />
              A new lot must pass validation QC before it can be put in use
            </label>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                        data-testid="reagent-notes-input" className="mt-1 min-h-[70px] border-slate-300" />
            </div>
            <Button type="submit" disabled={busy} data-testid="create-reagent-submit"
                    className="h-11 w-full bg-[#134e4a] hover:bg-[#0f3f3c]">
              {busy ? "Saving…" : "Add to catalog"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
